import { getSessionMetadata } from "./Helpers";
import { LoginVerificationType } from "models/Constants";
import { apiLives, MethodEnum, publicApi } from "./Api";
import { SDM } from "models/InitiateSessionResponse";
import { StorageKeys } from "./Authentication";

export interface IAuthMetadata {
  signature?: string;
  isAuthenticated: boolean;
}
export interface IPasswordlessStart {
  method: LoginVerificationType;
  sessionId?: string;
  email?: string;
  ignoreLastCode?: boolean;
}

export interface IAutoSignStart {
  sessionId?: string;
  e: string;
  s: string;
  o: string;
}

export interface ICompletePasswordlessStart {
  method: LoginVerificationType;
  code: string;
  sessionId?: string;
  email?: string;
  scope: string;
}

export interface IPhone {
  phone?: string;
  isMfa: boolean;
}
const authMetadataKey = "authMetadata";

class AuthService {
  _callbacks: Array<any> = [];
  _nextSubscriptionId = 0;
  _email?: string;
  _loginPageUri?: string;
  _phone?: IPhone;
  _key?: string;
  _SDMs?: ReadonlyArray<SDM>;
  _firstName?: string;
  _lastName?: string;
  _needsAuthSetup?: boolean;

  setEmail(email?: string) {
    this._email = email;
  }

  setKey(key?: string) {
    this._key = key;
  }

  setPhone(phone?: IPhone) {
    this._phone = phone;
  }

  setLoginPageUri(loginPageUri?: string) {
    this._loginPageUri = loginPageUri;
  }

  setFirstName(firstName: string) {
    this._firstName = firstName;
  }

  setLastName(lastName: string) {
    this._lastName = lastName;
  }

  setNeedsAuthSetup(needsAuthSetup: boolean) {
    this._needsAuthSetup = needsAuthSetup;
  }

  setSDMs(sdms?: Array<SDM>) {
    this._SDMs = sdms;
  }

  getPhone() {
    return this._phone;
  }

  getEmail() {
    return this._email;
  }

  getFirstName() {
    return this._firstName;
  }

  getLastName() {
    return this._lastName;
  }

  getKey() {
    return this._key;
  }

  hasEmail() {
    return !!this._email;
  }

  getLoginPageUri() {
    return this._loginPageUri;
  }

  getNeedsAuthSetup() {
    return this._needsAuthSetup;
  }

  getSDMs() {
    return this._SDMs;
  }

  async sessionAutoSignInStart(payload: IAutoSignStart) {
    return await publicApi<{ signature: string }>(
      "lives/$token",
      MethodEnum.Post,
      {
        ...payload,
      }
    );
  }

  async passwordLessSignInStart(payload: IPasswordlessStart) {
    return await publicApi("lives/$start-passwordless", MethodEnum.Post, {
      ...payload,
      key: this._key,
    });
  }

  async passwordLessSignInVerifyCode(payload: ICompletePasswordlessStart) {
    return await publicApi<{ signature: string }>(
      "lives/$complete-passwordless",
      MethodEnum.Post,
      { ...payload, key: this._key }
    );
  }

  async setupAuth() {
    const response = await publicApi<{
      hasPhoneIdentity: boolean;
      phone?: string | null;
    }>(`lives/${getSessionMetadata()?.lifeId}/$setup-auth`, MethodEnum.Post, {
      sessionId: getSessionMetadata()?.sessionId,
      sessionKey: getSessionMetadata()?.sessionKey,
      key: this._key,
    });
    if (response?.ok) {
      if (response.payload.hasPhoneIdentity) {
        this.setPhone({
          phone: response?.payload.phone!,
          isMfa: response?.payload.hasPhoneIdentity,
        });
      }
      return;
    } else {
      return response;
    }
  }

  saveAuthMetadata(signature?: string) {
    localStorage.setItem(
      authMetadataKey,
      JSON.stringify({
        signature: signature,
        isAuthenticated: !!signature,
      })
    );
  }

  private _getAuthMetadata() {
    const authMetadataValue = localStorage.getItem(authMetadataKey);
    let authMetadata: IAuthMetadata | undefined = undefined;
    if (authMetadataValue) {
      authMetadata = JSON.parse(authMetadataValue);
    }
    return authMetadata;
  }

  getAccessToken() {
    const authMetadata = this._getAuthMetadata();
    return authMetadata?.signature;
  }

  getIsAuthenticated() {
    const authMetadata = this._getAuthMetadata();
    return authMetadata?.isAuthenticated ?? false;
  }

  checkSession(callback: (result: boolean) => void) {}

  async logout(redirectUri?: string, isTimedOut?: boolean) {
    if (!isTimedOut) {
      localStorage.setItem("isLogoutClicked", "true");
    }
    sessionStorage.removeItem(StorageKeys.ManagedAccountLifeId);
    localStorage.removeItem("timeoutTime");
    if (this.getIsAuthenticated()) {
      await apiLives("$logout", MethodEnum.Post);
      localStorage.removeItem(authMetadataKey);
    }

    window.location = (redirectUri ??
      this._loginPageUri ??
      window.location.origin) as (string | Location) & Location;
  }

  static get instance() {
    return authService;
  }
}

const authService = new AuthService();

export default authService;
